// O APP NUNCA SOUBE O QUE É UMA ORGANIZAÇÃO — e a partir daqui ele sabe UMA
// coisa só: se quem está logado é MORADOR de uma.
//
// Medido em 17/08 antes de escrever: `org_id`, `org_role` e
// `organization_members` não aparecem em lugar nenhum de src/ neste
// repositório. O único uso de app_metadata é ler `provider`. Ou seja, o gate
// da Entrega 2 ("só aparece para membro com role='resident'") não tinha
// mecanismo — este arquivo é o mecanismo, e é o MENOR possível.
//
// ⛔ ESCOPO DELIBERADAMENTE MÍNIMO. Isto NÃO é o mundo do parceiro chegando ao
// app: não lê unidade, não lê organização, não lê nada agregado. Devolve um
// booleano. Tudo que a escalada precisa saber do lado do morador é "posso
// oferecer isto a você"; o resto (org_id, site_id) é carimbado no SERVIDOR por
// trigger, justamente para não vir do cliente.
//
// 🔴 A FONTE É O TOKEN, NÃO session.user.app_metadata. Esta é a lição de
// 15/08 do portal, paga em produção: o hook de identidade escreve os claims no
// JWT no momento da emissão, e o espelho em `user.app_metadata` NÃO fica
// sabendo. A primeira conta criada só pelo hook caiu no mundo errado por ler o
// espelho. Aqui a ordem é a mesma do portal: token primeiro, espelho como
// reserva.

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/** Decodifica o payload do JWT. Nunca lança — token torto vira `{}`. */
function jwtAppMetadata(accessToken: string | undefined): Record<string, unknown> {
  if (typeof accessToken !== "string") return {};
  const parts = accessToken.split(".");
  if (parts.length !== 3 || parts[1] === undefined) return {};
  try {
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json) as { app_metadata?: Record<string, unknown> };
    return payload.app_metadata ?? {};
  } catch {
    // Base64 inválido, JSON inválido, atob indisponível: um Espelho em branco
    // por causa de um claim é um preço que ninguém aceitaria pagar.
    return {};
  }
}

export interface OrgMembership {
  /** Membro de uma organização COM papel de morador. */
  isResident: boolean;
  /** Ainda resolvendo a sessão — não renderize o chip piscando. */
  loading: boolean;
}

export function useOrgMembership(): OrgMembership {
  const [state, setState] = useState<OrgMembership>({ isResident: false, loading: true });

  useEffect(() => {
    let active = true;

    const read = (session: { access_token?: string; user?: { app_metadata?: unknown } } | null) => {
      if (!active) return;
      if (session === null) {
        setState({ isResident: false, loading: false });
        return;
      }
      const fromToken = jwtAppMetadata(session.access_token);
      const mirror = (session.user?.app_metadata ?? {}) as Record<string, unknown>;
      const meta = Object.keys(fromToken).length > 0 ? fromToken : mirror;
      const orgId = typeof meta["org_id"] === "string" ? meta["org_id"] : "";
      const role = typeof meta["org_role"] === "string" ? meta["org_role"] : "";
      setState({ isResident: orgId !== "" && role === "resident", loading: false });
    };

    void supabase.auth.getSession().then(({ data }) => read(data.session));
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => read(session));

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  return state;
}
